<template>
  <Teleport :to="hasAppTarget ? '#app' : 'body'" :disabled="!hasAppTarget">
    <Transition name="full-screen-panel">
      <section
        v-if="modelValue"
        ref="panel"
        class="full-screen-panel fixed inset-0 z-50 flex flex-col bg-white outline-none"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        tabindex="-1"
        :data-testid="testId"
        @keydown="onPanelKeydown"
      >
        <header
          class="shrink-0 border-b border-gray-200 bg-white px-2 pt-[calc(0.5rem+env(safe-area-inset-top,0px))] pb-2"
          :data-testid="`${testId}-header`"
        >
          <div class="flex items-start gap-1">
            <button
              type="button"
              class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg text-gray-700"
              aria-label="Voltar"
              :data-testid="`${testId}-back`"
              @click="close"
            >
              ←
            </button>
            <div class="min-w-0 flex-1 pt-1.5 text-left">
              <h2 class="truncate text-base font-bold text-gray-900">{{ title }}</h2>
              <p v-if="meta" class="text-xs text-gray-500" :data-testid="`${testId}-meta`">
                {{ meta }}
              </p>
              <div v-if="$slots['header-extra']" class="mt-1.5 flex flex-wrap gap-1">
                <slot name="header-extra" />
              </div>
            </div>
          </div>
        </header>
        <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-3">
          <slot />
        </div>
        <footer
          v-if="$slots.dock"
          class="shrink-0 border-t border-gray-200 bg-white px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]"
          :data-testid="`${testId}-dock`"
        >
          <slot name="dock" />
        </footer>
      </section>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, toRef } from 'vue'
import { useOverlayA11y } from '@/composables/useOverlayA11y'

/** Tela cheia do celular (spec §4.3): cabeçalho fixo (←, título, meta) e dock inferior fixo. */
const props = withDefaults(
  defineProps<{ modelValue: boolean; title: string; meta?: string; testId?: string }>(),
  { meta: undefined, testId: 'full-screen-panel' }
)
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()
const panel = ref<HTMLElement | null>(null)

// O Tailwind só vale dentro de #app (`important: '#app'`). Sem #app (jsdom), renderiza no lugar.
// O Vue 3.3 resolve o `to` mesmo com o Teleport desabilitado; `body` sempre existe e evita o aviso.
const hasAppTarget = typeof document !== 'undefined' && !!document.getElementById('app')

function close(): void {
  emit('update:modelValue', false)
}

const { onPanelKeydown } = useOverlayA11y({
  panel,
  isOpen: toRef(props, 'modelValue'),
  onEscape: close
})
</script>

<style scoped>
.full-screen-panel-enter-active,
.full-screen-panel-leave-active {
  transition: transform 220ms ease;
}
.full-screen-panel-enter-from,
.full-screen-panel-leave-to {
  transform: translateX(100%);
}
@media (prefers-reduced-motion: reduce) {
  .full-screen-panel-enter-active,
  .full-screen-panel-leave-active {
    transition: none;
  }
}
</style>
